import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Wallet, PhoneCall, Clock } from "lucide-react";
import api from "@/lib/api";
import { PublicShell } from "@/components/PublicShell";
import { CALL_DESTINATIONS } from "@/data/callDestinations";

const useLocalTime = (tz) => {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!tz) return;
    const tick = () => {
      try {
        const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true }).format(new Date());
        const hour = parseInt(new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", hour12: false }).format(new Date()), 10);
        setT({ str: parts, isNight: hour >= 21 || hour < 7 });
      } catch { setT(null); }
    };
    tick();
    const i = setInterval(tick, 30000);
    return () => clearInterval(i);
  }, [tz]);
  return t;
};

export default function CallCountry({ dest }) {
  const [rate, setRate] = useState(null);
  const time = useLocalTime(rate?.tz);

  useEffect(() => {
    api.get("/rates").then(({ data }) => {
      setRate(data.find((r) => r.country === dest.country) || null);
    }).catch(() => {});
  }, [dest.country]);

  useEffect(() => {
    document.title = `Cheap Calls to ${dest.country} ${rate ? `— $${rate.rate.toFixed(3)}/min ` : ""}| ZEEPTEL`;
    const m = document.querySelector('meta[name="description"]');
    const prev = m?.getAttribute("content");
    m?.setAttribute("content", `Call ${dest.country} (${dest.dial}) for less with ZEEPTEL. ${dest.blurb} Prepaid credit that never expires — no internet needed on the other end.`);
    return () => {
      document.title = "ZEEPTEL — Global Calling Credit";
      if (m && prev) m.setAttribute("content", prev);
    };
  }, [dest, rate]);

  const others = CALL_DESTINATIONS.filter((d) => d.slug !== dest.slug);

  return (
    <PublicShell>
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-16">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Cheap international calls to {dest.country} {dest.flag}
        </h1>
        <p className="mt-3 text-slate-500">{dest.blurb}</p>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="country-rate-card">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">ZEEPTEL rate to {dest.country}</div>
            <div className="mt-1 font-mono text-3xl font-bold text-[#2563EB]">
              {rate ? `$${rate.rate.toFixed(3)}` : "—"}<span className="text-base text-slate-400 font-semibold">/min</span>
            </div>
            <div className="mt-1 text-xs text-slate-400">Billed per minute from your prepaid wallet · dial code {dest.dial}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="country-time-card">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
              <Clock className="h-3 w-3" /> Local time in {dest.country}
            </div>
            <div className="mt-1 font-mono text-3xl font-bold text-slate-900">{time ? time.str : "—"}</div>
            <div className="mt-1 text-xs text-slate-400">{time?.isNight ? "🌙 It's late there — they may be asleep" : "☀️ Good time to call"}</div>
          </div>
        </div>

        <h2 className="mt-10 font-heading text-lg font-bold text-slate-900">How calling {dest.country} works</h2>
        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          {[
            { icon: Wallet, title: "1. Add credit", desc: "Create a free account and top up your wallet by card — credit never expires." },
            { icon: Phone, title: "2. Dial the number", desc: `Enter any ${dest.country} number (${dest.dial}…) in the ZEEPTEL dialer.` },
            { icon: PhoneCall, title: "3. Answer & talk", desc: "We ring YOUR phone first, then bridge you — the person you call needs no internet or app." },
          ].map((s) => (
            <div key={s.title} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <s.icon className="h-5 w-5 text-[#2563EB]" />
              <div className="mt-2 font-bold text-sm text-slate-900">{s.title}</div>
              <div className="mt-1 text-xs text-slate-500 leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>

        <h2 className="mt-10 font-heading text-lg font-bold text-slate-900">Frequently asked questions</h2>
        <div className="mt-4 space-y-3">
          {[
            [`How do I call ${dest.country} from abroad?`, `Sign up for a free ZEEPTEL account, add prepaid credit, then dial the full international number starting with ${dest.dial}. ZEEPTEL calls your own phone first and connects you — the whole call runs over the normal phone network.`],
            [`Does the person in ${dest.country} need internet or an app?`, `No. Calls arrive as a regular phone call on any mobile or landline in ${dest.country} — it works even on 2G networks and in rural areas.`],
            [`What is the dialing code for ${dest.country}?`, `The country code for ${dest.country} is ${dest.dial}. In the ZEEPTEL dialer it is filled in automatically when you pick ${dest.country}.`],
            ["Does my credit expire?", "Never. Your ZEEPTEL wallet balance stays available until you use it — no monthly fees, no expiry."],
          ].map(([q, a]) => (
            <details key={q} className="bg-white border border-slate-200 rounded-2xl px-5 py-4 shadow-sm group">
              <summary className="font-semibold text-sm text-slate-900 cursor-pointer list-none flex items-center justify-between">
                {q} <span className="text-slate-300 group-open:rotate-90 transition-transform">›</span>
              </summary>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{a}</p>
            </details>
          ))}
        </div>

        <div className="mt-10 bg-[#1E3A8A] text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="country-cta">
          <div>
            <div className="font-heading font-bold text-lg">Start calling {dest.country} in under a minute</div>
            <div className="text-blue-200 text-sm">Free account · pay only for the minutes you use · credit never expires.</div>
          </div>
          <Link to="/register" data-testid="country-cta-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-white text-[#1E3A8A] font-bold text-sm hover:bg-blue-50 transition-colors shrink-0">
            <Phone className="h-4 w-4" /> Get Started <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <h2 className="mt-10 font-heading text-sm font-bold uppercase tracking-widest text-slate-400">More destinations</h2>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="related-destinations">
          {others.map((d) => (
            <Link key={d.slug} to={`/call-${d.slug}`} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:border-[#2563EB] hover:text-[#2563EB] transition-colors">
              {d.flag} Call {d.country}
            </Link>
          ))}
          <Link to="/rates" className="px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition-colors">
            All rates →
          </Link>
        </div>
      </main>
    </PublicShell>
  );
}
